/**
 * UnreadEventsContext
 *
 * Fuente única de verdad para los eventos no leídos conectada a RTK Query (eventApi).
 * - Sincronización automática de caché de Redux.
 * - Sonido de alarma y actualización quirúrgica ante eventos WebSocket en tiempo real.
 * - Re-sincronización tras reconexión.
 * - Expone `allEvents`, `hasUnread` y `refresh()`.
 */

import React, {
  createContext,
  useCallback,
  useContext,
} from 'react';
import { useDispatch } from 'react-redux';
import { useGetAllEventsNotReadQuery, eventApi } from '../redux/api/eventApi';
import { useWebSocketEvent } from './WebSocketContext';

interface UnreadEventsContextType {
  allEvents: any[];
  hasUnread: boolean;
  refresh: () => void;
}

const UnreadEventsContext = createContext<UnreadEventsContextType>({
  allEvents: [],
  hasUnread: false,
  refresh: () => {},
});

export function UnreadEventsProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const { data: allEvents = [], refetch } = useGetAllEventsNotReadQuery();

  // WebSocket: Nuevos eventos en tiempo real (reproduce alarma y actualiza caché)
  useWebSocketEvent(
    'NEW_EVENT',
    useCallback(
      (newEvent: any) => {
        if (!newEvent) return;
        dispatch(
          (eventApi.util as any).updateQueryData('getAllEventsNotRead', undefined, (draft: any[]) => {
            const exists = draft.some((ev: any) => String(ev.eventId) === String(newEvent.eventId));
            if (!exists) {
              draft.unshift(newEvent);
            }
          })
        );

        // Alarma sonora crítica: Solo reproducir sirena para emergencias críticas inmediatas P0 (anti-fatiga de alarmas)
        const isCriticalP0 =
          newEvent.priority === 'P0' ||
          ['FALLDOWN', 'SHOCK', 'GEO_FENCE'].includes(newEvent.name);

        if (isCriticalP0) {
          try {
            const audio = new Audio('/audio/alarm.mp3');
            audio.play().catch(() => {});
          } catch (_) {}
        }
      },
      [dispatch],
    ),
  );

  // WebSocket: Re-sincronizar tras reconexión
  useWebSocketEvent(
    '_RECONNECT_',
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  const hasUnread = allEvents.some((ev: any) => !ev.read);

  return (
    <UnreadEventsContext.Provider
      value={{ allEvents, hasUnread, refresh: refetch }}
    >
      {children}
    </UnreadEventsContext.Provider>
  );
}

/**
 * Hook de consumo. Lanza error descriptivo si se usa fuera del provider.
 */
export function useUnreadEvents() {
  const ctx = useContext(UnreadEventsContext);
  if (!ctx) {
    throw new Error('useUnreadEvents must be used inside <UnreadEventsProvider>');
  }
  return ctx;
}
